import type { TOC } from '@ember/component/template-only';
import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import {
  PrismBadge,
  PrismBanner,
  PrismLoadingBar,
  PrismMessage,
  PrismSelectInput,
  PrismToggle,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import ParityButtonGroup from 'lumen/components/parity/button-group';
import ParityChartsChartIssueType from 'lumen/components/parity/charts/chart-issue-type';
import ParityChartsChartVariance from 'lumen/components/parity/charts/chart-variance';
import ParityInsightBreakdownBlock from 'lumen/components/parity/insight/breakdown-block';
import ParityInsightFixedKpiHeader from 'lumen/components/parity/insight/fixed-kpi-header';
import ParityInsightHistoricalBlock from 'lumen/components/parity/insight/historical-block';
import ParityInsightSelections from 'lumen/components/parity/insight/selections';
import ParityTablesTableIssues from 'lumen/components/parity/tables/table-issues';
import type HotelParityInsightController from 'lumen/controllers/hotel/parity/insight';
import {
  FILTER_BAR_PAGETYPES,
  HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import { PI_BREAKDOWNS } from 'lumen/utils/constants/pi-constant-values';

const LOADING_BAR_POSITION = HEADER_HEIGHT + SUB_HEADER_HEIGHT;

interface HotelParityInsightSignature {
  Args: {
    controller: HotelParityInsightController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader>
    <div class="content-wrapper flex items-center">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <GeneralDatepickersDatepickerRange
          @calendarFocusOnEnd={{true}}
          @maxDate={{@controller.maxDateMoment}}
          @minDate={{@controller.minDateMoment}}
          {{! @glint-expect-error: Incorrect type }}
          @range={{@controller.dateRange}}
          @rangeChanged={{@controller.periodChangedFromDatePicker}}
          @selectStandardRange={{true}}
          @standardRange={{@controller.isoDateAggregation}}
        />
      </div>

      <PrismSelectInput
        @onChange={{@controller.dateAggregationChanged}}
        @options={{@controller.timespans}}
        @value={{@controller.selectedTimespan}}
      />

      <GeneralFilterBarFilterBar
        @activeFilters={{@controller.activeFilters}}
        @favourites={{@controller.favourites}}
        @filterBarSegmentationClicked={{@controller.segmentationFilterClickedTask.perform}}
        @hotel={{@controller.hotelAndMarketService.hotel}}
        @isHovered={{@controller.isFilterBarHovered}}
        @isLoading={{@controller.isLoading}}
        @market={{@controller.hotelAndMarketService.market}}
        @pageType={{FILTER_BAR_PAGETYPES.PARITY_ANALYTICS}}
        @segmentationFilters={{@controller.segmentationFilters}}
        {{! @glint-expect-error: Incorrect type }}
        @setFilterValues={{@controller.setFilterValues}}
        {{on "mouseenter" (fn @controller.setIsFilterBarHovered true)}}
        {{on "mouseleave" (fn @controller.setIsFilterBarHovered false)}}
      />

      <ParityButtonGroup />
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center">
      <span id="ic-product-tour-56605327-member-rate-toggle">
        <PrismToggle
          @isChecked={{@controller.showMemberRate}}
          @label={{t "parity_strategy.membership.member"}}
          @labelPosition="before"
          @onChange={{@controller.toggleMemberRate}}
        />
        <PrismTooltip
          @side="bottom"
          @text={{t "parity.member_rate_toggle.info"}}
        />
      </span>
      <GeneralFilterBarDivider />
      <ParityInsightSelections
        @config={{@controller.model.config}}
        @selectedLos={{@controller.los}}
        @selectedMealPlan={{@controller.mealPlan}}
        @selectedPos={{@controller.pos}}
        @selectedRateType={{@controller.rateType}}
        @selectionsChanged={{@controller.selectionsChanged}}
      />
    </div>
  </GeneralPageHeader>

  <ParityInsightFixedKpiHeader
    @data={{@controller.selectedPeriodData}}
    @hideHotelsMetric={{true}}
  />

  <GeneralPageMain
    {{didUpdate
      @controller.displayedSegmentationFiltersObserver
      @controller.segmentationFilters
      @controller.isLoading
    }}
  >
    {{#if @controller.isLoading}}
      <PrismLoadingBar
        @isFixed={{true}}
        @positionTop={{LOADING_BAR_POSITION}}
      />
    {{/if}}

    <div class="parity-analytics content-wrapper main-content grid gap-800">
      <section class="grid gap-400">
        <h2>{{t "parity_analytics.headers.evolution"}}</h2>

        {{#if @controller.hasHistoricalRequestError}}
          <div class="grid h-[440px] place-items-center">
            <PrismMessage
              {{! @glint-expect-error: Incorrect type }}
              @description={{@controller.model.historicalData.error.message}}
              @illustration="100x100_critical"
              @title={{t "general.error_title"}}
            />
          </div>
        {{else}}
          <ParityInsightHistoricalBlock
            @dateAggregation={{@controller.dateAggregation}}
            @extractDate={{@controller.extractDate}}
            @extractDateKey={{@controller.extractDateKey}}
            @hideHotelsMetric={{true}}
            @historicalChartData={{@controller.historicalChartData}}
            @historicalExtractDate={{@controller.historicalExtractDate}}
            @historicalExtractDateMoment={{@controller.historicalExtractDateMoment}}
            @isBenchmarkVisible={{@controller.isBenchmarkVisible}}
            @isLoading={{@controller.isLoadingHistoricalData}}
            @isLoadingGaugeData={{@controller.isLoadingGaugeData}}
            @maxDate={{@controller.maxDate}}
            @parityFilters={{@controller.parityFilters}}
            @periodChanged={{@controller.periodChanged}}
            @updateHistoricalPeriod={{@controller.updateHistoricalPeriod}}
            @updateParityFilters={{@controller.updateParityFilters}}
            @widgetData={{@controller.selectedPeriodData}}
          />
        {{/if}}
      </section>

      <section class="grid gap-400">
        <h2>{{@controller.formattedExtractRange}}</h2>

        {{#if @controller.hasSegmentationRequestError}}
          <div class="grid place-items-center">
            <PrismMessage
              {{! @glint-expect-error: Incorrect type }}
              @description={{@controller.model.segmentationData.error.message}}
              @illustration="100x100_critical"
              @title={{t "general.error_title"}}
            />
          </div>
        {{else if (or @controller.isLoading @controller.allData)}}
          <section class="grid gap-400">
            <h3>
              {{t "parity_analytics.headers.issue_price_diff"}}
            </h3>

            <div class="insight-grid">
              <ParityChartsChartIssueType
                @hideHotelsMetric={{true}}
                @issueTypeData={{@controller.issueTypeData}}
                {{! @glint-expect-error: Incorrect type }}
                @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                @segmentationFilterClicked={{@controller.segmentationFilterClickedTask.perform}}
                @showLoadingPlaceholderState={{@controller.isLoadingSegmentationData}}
                @totalLoss={{@controller.allData.value.loss}}
              />

              <ParityChartsChartVariance
                @allVariances={{@controller.varianceData}}
                @hideHotelsMetric={{true}}
                {{! @glint-expect-error: Incorrect type }}
                @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                @segmentationFilterClicked={{@controller.segmentationFilterClickedTask.perform}}
                @showLoadingPlaceholderState={{@controller.isLoadingSegmentationData}}
                @totalNbOfShops={{@controller.allData.calculated_values.totalNbOfShops}}
                class="col-span-2"
              />
            </div>
          </section>

          <section class="grid gap-400">
            <div class="flex items-center justify-between">
              <h3>{{t "parity_analytics.headers.segments"}}</h3>

              <div
                class="section-title-group text-neutral-subdued ml-300 flex items-center gap-200"
              >
                <p class="text-200--regular">
                  {{t "parity_analytics.sortBy"}}:
                </p>
                <PrismSelectInput
                  @horizontalPosition="right"
                  @onChange={{@controller.sortKeyChanged}}
                  @options={{@controller.sortKeys}}
                  @value={{@controller.selectedSortKey}}
                />
                <PrismSelectInput
                  @horizontalPosition="right"
                  @onChange={{@controller.sortTypeChanged}}
                  @options={{@controller.sortTypes}}
                  @value={{@controller.selectedSortType}}
                />
              </div>
            </div>

            <div class="insight-grid grid-cols-4">
              {{#if @controller.isLoadingSegmentationData}}
                <IncrementFor @times={{4}}>
                  {{! @glint-expect-error: Incorrect type }}
                  <ParityInsightBreakdownBlock @isLoading={{true}} />
                </IncrementFor>
              {{else}}
                {{#each @controller.segmentationBlocks as |segmentation index|}}
                  <ParityInsightBreakdownBlock
                    @hideHotelsMetric={{true}}
                    @isLoading={{@controller.isLoadingSegmentationData}}
                    @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                    @redirectToTrends={{@controller.redirectToTrends}}
                    @searchValues={{get
                      @controller.searchValuesPerSegmentationId
                      segmentation.segmentationName
                    }}
                    @segmentationBlockIdx={{index}}
                    @segmentationData={{segmentation}}
                    @segmentationFilterClicked={{@controller.segmentationFilterClickedTask.perform}}
                    @setIsSearchActive={{@controller.setIsSearchActive}}
                    @setSearchValue={{@controller.setSearchValue}}
                    @showLoadingPlaceholderState={{@controller.isLoadingSegmentationData}}
                    @sortKey={{@controller.sortKey}}
                    @sortType={{@controller.sortType}}
                    class="parity
                      {{if
                        (eq
                          segmentation.segmentationName PI_BREAKDOWNS.PLATFORM
                        )
                        'ic-product-tour-pi-platform-split'
                      }}"
                  />
                {{/each}}
              {{/if}}
            </div>
          </section>
        {{else}}
          <div class="grid place-items-center">
            <PrismMessage
              @description=""
              @illustration="100x100_empty"
              @title={{t "parity_analytics.no_data"}}
            />
          </div>
        {{/if}}

        <section class="grid gap-400">
          <div class="flex items-center gap-200">
            <h3>{{t "general.issues" count=2}}</h3>

            <PrismBadge @label={{@controller.amountOfIssues}} @type="info" />
          </div>

          {{#if @controller.hasIssuesAndScreenshotParitiesRequestError}}
            <div class="grid place-items-center">
              <PrismMessage
                {{! @glint-expect-error: Incorrect type }}
                @description={{@controller.model.issuesAndScreenshotParities.error.message}}
                @illustration="100x100_critical"
                @title={{t "general.error_title"}}
              />
            </div>
          {{else}}
            <PrismBanner
              @bannerMessage={{t "parity_strategy.issues_table_info_banner"}}
              @bannerStyle="info"
              @icon="circle-information-outline"
            />

            <ParityTablesTableIssues
              @isLoading={{@controller.isLoadingIssuesAndScreenshotParities}}
              @issues={{@controller.filteredIssues}}
            />
          {{/if}}
        </section>
      </section>

      {{outlet}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelParityInsightSignature>;
